import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { ButtonShadcn } from "@/components/ui/Button";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import RHFTextArea from "@/components/form/RHFTextArea";
import { applyServerFieldErrors } from "@/utils/formErrorUtils";
import { clearDraft, readDraft, writeDraft } from "@/lib/draftStorage";
import { NOTE_BODY_MAX_LENGTH, noteSchema } from "../noteSchema";
import { LEGEND_NEW, SCOPE_LABEL, THIS_JOB, EVERY_JOB } from "../scope";

/** The fields this form has an input for, so a 422 naming anything else still
 *  reaches the reader through the owner's toast rather than vanishing. */
const FIELDS = ["body"];

/** "Rahul Mehta" → "Rahul". The placeholder is a sentence, not a record. */
const firstName = (name = "") => name.trim().split(/\s+/)[0] || "";

/**
 * Writes one note.
 *
 * **The order is the order of the decision:** choose where the note goes, write
 * it, post it. The choice used to sit between the field and the button, one
 * hairline above the stream, and a reader shown that layout took it for a filter
 * over the notes below. Its legend is a verb for the same reason — *add this
 * note to* is not something a filter says.
 *
 * **The two options are parallel.** They used to read "This job" against "The
 * candidate" — a job set against a person, so the reader had to work out that
 * one of them meant *every* job. `Only this job` against `All their jobs` is the same
 * decision in one dimension. Nothing under them changes as you pick, because a
 * hint that rewrites itself is a third thing to read.
 *
 * It defaults to **this job**, the narrower of the two. A note filed too narrow
 * is merely quiet, and since the scope is editable afterwards it is a mistake
 * with a way back; a note filed too wide has already put one job's business on
 * a person's permanent record, and no edit un-reads it.
 *
 * With no application on screen there is nothing to choose between, so the
 * control is absent and every note is about the person.
 *
 * `mode: "onChange"` puts the message on screen as you type. `disabled={!isValid}`
 * makes the button unpressable while it is showing. Neither works without the
 * other, and the pair is the whole input rule. A body of spaces trims to nothing
 * and counts as empty, on this side as well as the server's.
 *
 * The field clears only after `onSubmit` resolves. A note lost to a network
 * blip is the worst outcome this screen has, and `draftKey` is the other half of
 * that: the Notes tab is a panel, so switching to Activity unmounts this form,
 * and a half-written note used to go with it. The draft keeps the scope as well
 * as the words — a note restored under the wrong scope is worse than no draft at
 * all. `lib/draftStorage.js` says why it lives in `sessionStorage` and how
 * logout clears it.
 *
 * @param {string|null} applicationId  the application this screen was opened on
 * @param {string} [jobTitle]          its job, named in the option's tooltip
 * @param {string} [candidateName]     whose page this is, for the placeholder
 * @param {string} [draftKey]          where to keep an unsent draft; omit to keep none
 * @param {Function} onSubmit          ({applicationId, body}) — may reject; the owner reports why
 */
const NoteComposer = ({ applicationId = null, jobTitle = "", candidateName = "", draftKey = "", onSubmit }) => {
  const saved = useRef(readDraft(draftKey)).current;

  const [scope, setScope] = useState(saved?.scope ?? (applicationId ? THIS_JOB : EVERY_JOB));

  const methods = useForm({
    resolver: zodResolver(noteSchema),
    defaultValues: { body: saved?.body ?? "" },
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { isSubmitting, isValid },
  } = methods;

  const body = watch("body");

  // Every keystroke, and the scope with it. A body of spaces is nothing, the
  // same rule `noteSchema` applies, and passing `null` clears the key rather
  // than leaving a draft the next mount restores into a form that then looks
  // untouched and invalid. Writing a short string to `sessionStorage` is cheap
  // enough not to debounce.
  useEffect(() => {
    writeDraft(draftKey, body?.trim() ? { body, scope } : null);
  }, [draftKey, body, scope]);

  // `noteSchema` already trimmed the body, so nothing here trims it again.
  // A rejected `onSubmit` leaves the words on screen and clears nothing.
  //
  // A 422 about the body belongs on the body, not only in the owner's toast.
  // `applyServerFieldErrors` puts it there and takes the focus; it can only
  // happen if this form and `_clean_body` ever disagree, which is exactly when
  // you want to see which field the server meant.
  const submit = async ({ body: cleaned }) => {
    try {
      await onSubmit?.({ applicationId: scope === THIS_JOB ? applicationId : null, body: cleaned });
      reset({ body: "" });
      clearDraft(draftKey);
    } catch (err) {
      applyServerFieldErrors(err, setError, FIELDS);
    }
  };

  // Ctrl/⌘+Enter files the note. A plain Enter still breaks the line, because
  // a note is a paragraph and losing one to a stray keystroke is worse than
  // reaching for the button.
  const onKeyDown = (event) => {
    if (event.key !== "Enter" || !(event.metaKey || event.ctrlKey)) return;
    event.preventDefault();
    if (!isSubmitting && isValid) handleSubmit(submit)();
  };

  const who = firstName(candidateName);
  const disabledReason = isSubmitting ? "Adding your note…" : !isValid ? "Write something first." : undefined;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submit)} onKeyDown={onKeyDown} noValidate>
        {applicationId && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-b border-border-primary/70 pb-3">
            <span className="text-xs font-medium text-text-secondary">{LEGEND_NEW}</span>
            <SegmentedControl
              options={[
                // The tooltip names the job. Both labels stay two words, so the
                // pair reads as one decision rather than a job title set
                // against a phrase.
                { value: THIS_JOB, label: SCOPE_LABEL[THIS_JOB], title: jobTitle || undefined },
                { value: EVERY_JOB, label: SCOPE_LABEL[EVERY_JOB] },
              ]}
              value={scope}
              onChange={setScope}
              disabled={isSubmitting}
              ariaLabel={LEGEND_NEW}
            />
          </div>
        )}

        <RHFTextArea
          id="note-composer-body"
          name="body"
          label="Write a note"
          hideLabel
          placeholder={who ? `Write a note about ${who}…` : "Write a note about this candidate…"}
          rows={3}
          autoResize
          counter="near-limit"
          maxLength={NOTE_BODY_MAX_LENGTH}
          disabled={isSubmitting}
          inputClass="min-h-[84px] max-h-[160px] overflow-y-auto custom-scrollbar leading-relaxed rounded-xl border-border-primary bg-background-primary sm:max-h-[240px]"
        />

        <div className="mt-3 flex items-center justify-end gap-2 border-t border-border-primary/70 pt-3">
          <ButtonShadcn type="submit" variant="secondary" size="lg" disabled={isSubmitting || !isValid} title={disabledReason}>
            {isSubmitting ? "Adding…" : "Add note"}
          </ButtonShadcn>
        </div>
      </form>
    </FormProvider>
  );
};

export default NoteComposer;